import React, { useState, useEffect } from 'react';
import ChartCard from './ChartCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchPreApprovalStats } from '../../features/dashboard/dashboardSlice';

const PreapprovalChart: React.FC = () => {
  const dispatch = useAppDispatch();
  const { preApprovalStats: stats, loadingPreApprovalStats: isLoading, hasFetchedPreApprovalStats } = useAppSelector(state => state.dashboard);
  const [timeFrame, setTimeFrame] = useState<'today' | 'thisWeek' | 'thisMonth'>('today');

  useEffect(() => {
    if (!hasFetchedPreApprovalStats && !isLoading) {
      dispatch(fetchPreApprovalStats());
    }
  }, [dispatch, hasFetchedPreApprovalStats, isLoading]);

  const formatChartData = () => {
    if (!stats) return [];

    const period = stats[timeFrame];

    return [
      {
        name: 'Cashe',
        Approved: period.cashe.approved.count,
        ApprovedPercentage: period.cashe.approved.percentage,
        Rejected: period.cashe.rejected.count,
        RejectedPercentage: period.cashe.rejected.percentage,
      },
      {
        name: 'Zype',
        Approved: period.zype.offerExists.count,
        ApprovedPercentage: period.zype.offerExists.percentage,
        Rejected: period.zype.noOffer.count,
        RejectedPercentage: period.zype.noOffer.percentage,
      }
    ];
  };

  const chartData = !isLoading && stats ? formatChartData() : [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataName = payload[0].name === "Approved" ? "Approved" : "Rejected";
      const color = dataName === "Approved" ? "#10B981" : "#EF4444";

      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
          <p className="font-gilroy-semibold text-gray-700 mb-1">Partner: {label}</p>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: color }}></div>
            <p className="font-gilroy-semibold" style={{ color }}>
              {dataName}: {payload[0].value}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ChartCard 
      title="Preapproval Stats" 
      subtitle="Zype and Cashe approvals"
      filters={
        <select 
          className="text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={timeFrame}
          onChange={(e) => setTimeFrame(e.target.value as 'today' | 'thisWeek' | 'thisMonth')}
          disabled={isLoading}
        >
          <option value="today">Today</option>
          <option value="thisWeek">This Week</option>
          <option value="thisMonth">This Month</option>
        </select>
      }
      className="h-full"
    >
      {isLoading ? (
        <div className="h-64 w-full flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading preapproval data...</div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              barGap={4}
              barSize={20}
            >
              <defs>
                <linearGradient id="approvedGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
                <linearGradient id="rejectedGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#EF4444" />
                  <stop offset="100%" stopColor="#DC2626" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
              <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 14 }} width={80} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(236, 236, 236, 0.2)' }} />
              <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }} iconType="circle" iconSize={8} />
              <Bar dataKey="Approved" fill="url(#approvedGradient)" radius={[0, 4, 4, 0]} stackId="a">
                <LabelList dataKey="ApprovedPercentage" position="right" style={{ fill: '#10B981', fontWeight: 600, fontSize: 12 }} />
              </Bar>
              <Bar dataKey="Rejected" fill="url(#rejectedGradient)" radius={[0, 4, 4, 0]} stackId="a">
                <LabelList dataKey="RejectedPercentage" position="right" style={{ fill: '#EF4444', fontWeight: 600, fontSize: 12 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 w-full flex items-center justify-center">
          <div className="text-gray-500">No preapproval data available for the selected time period.</div>
        </div>
      )}
    </ChartCard>
  );
};

export default PreapprovalChart;
